import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';

import { useUpsertEvent } from '@/hooks/useEvent';
import { ESTILO_DEFAULT } from '@/invitation/payload/styles';
import type { EstiloId } from '@/invitation/payload/types';

// Wizard components
import WizardLayout from '@/components/wizard/WizardLayout';
import WelcomeStep from '@/components/wizard/steps/WelcomeStep';
import CoupleStep from '@/components/wizard/steps/CoupleStep';
import StyleStep from '@/components/wizard/steps/StyleStep';

/**
 * 011 — Asistente de creación de invitaciones (solo `/invitations/new`).
 * Flujo mínimo: Inicio → Pareja (nombres + fecha, fuente única = Evento 006)
 * → Estilo (7 variantes DS v2 con preview real, elegido al final) → Crear.
 * Sin colores personalizados, sin pasos de Lugares/Detalles (viven en el editor).
 */

const WIZARD_STEPS = [
  { id: 'welcome', title: 'Inicio' },
  { id: 'couple', title: 'Pareja' },
  { id: 'style', title: 'Estilo' },
] as const;

interface WizardFormData {
  bride_name: string;
  groom_name: string;
  wedding_date: string; // YYYY-MM-DD (date-only, TZ-safe)
  template_id: EstiloId;
}

const InvitationWizard = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const upsertEvent = useUpsertEvent();

  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState<WizardFormData>({
    bride_name: '',
    groom_name: '',
    wedding_date: '',
    template_id: ESTILO_DEFAULT,
  });

  // El wizard es SOLO de creación (G1). Si llega con un id (ruta :id/wizard),
  // se redirige al editor por pestañas — la edición no vive aquí.
  useEffect(() => {
    if (id && id !== 'new') {
      navigate(`/invitations/${id}/edit`, { replace: true });
    }
  }, [id, navigate]);

  const updateFormData = (updates: Partial<WizardFormData>) => {
    setFormData((prev) => ({ ...prev, ...updates }));
  };

  const currentStepId = WIZARD_STEPS[currentStep].id;
  const isCoupleStep = currentStepId === 'couple';

  const coupleComplete =
    Boolean(formData.bride_name.trim()) &&
    Boolean(formData.groom_name.trim()) &&
    Boolean(formData.wedding_date);

  const handleNext = async () => {
    if (isCoupleStep) {
      if (!coupleComplete) {
        toast.error('Por favor completa los nombres y la fecha');
        return;
      }
      try {
        // 006 — nombres/fecha se escriben en el Evento (fuente única); lo crea si no existe.
        await upsertEvent.mutateAsync({
          bride_name: formData.bride_name,
          groom_name: formData.groom_name,
          wedding_date: formData.wedding_date || null,
        });
      } catch {
        // El error lo notifica la mutación (toast).
        return;
      }
    }

    setCurrentStep((prev) => Math.min(prev + 1, WIZARD_STEPS.length - 1));
  };

  const handleBack = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 0));
  };

  const handleStepClick = (stepIndex: number) => {
    if (stepIndex <= currentStep) {
      setCurrentStep(stepIndex);
    }
  };

  const getStepHint = () => {
    switch (currentStepId) {
      case 'couple':
        return 'Estos datos aparecerán en tu invitación';
      case 'style':
        return 'Elige el estilo que más los represente';
      default:
        return undefined;
    }
  };

  const renderStep = () => {
    switch (currentStepId) {
      case 'welcome':
        return <WelcomeStep onStart={() => setCurrentStep(1)} />;
      case 'couple':
        return (
          <CoupleStep
            brideName={formData.bride_name}
            groomName={formData.groom_name}
            weddingDate={formData.wedding_date}
            onBrideNameChange={(value) => updateFormData({ bride_name: value })}
            onGroomNameChange={(value) => updateFormData({ groom_name: value })}
            onWeddingDateChange={(value) => updateFormData({ wedding_date: value })}
          />
        );
      case 'style':
        return (
          <StyleStep
            selectedStyle={formData.template_id}
            onSelectStyle={(estilo) => updateFormData({ template_id: estilo })}
            brideName={formData.bride_name}
            groomName={formData.groom_name}
            weddingDate={formData.wedding_date}
          />
        );
      default:
        return null;
    }
  };

  return (
    <WizardLayout
      steps={WIZARD_STEPS as unknown as { id: string; title: string }[]}
      currentStep={currentStep}
      onStepClick={handleStepClick}
      onBack={handleBack}
      onNext={handleNext}
      showBack={currentStep > 0}
      // El paso Estilo tiene su propio botón "Crear"; Inicio tiene su propio CTA.
      showNext={isCoupleStep}
      nextLabel="Continuar"
      isNextDisabled={!coupleComplete || upsertEvent.isPending}
      showProgress={currentStep > 0}
      stepHint={getStepHint()}
    >
      {renderStep()}
    </WizardLayout>
  );
};

export default InvitationWizard;
